export type ActionType = "search" | "cardClick" | "suggestedQuestionClick";

interface StoredAction {
  type: ActionType;
  timestamp: number;
}

interface ActionCounts {
  search: number;
  cardClick: number;
  suggestedQuestionClick: number;
}

const STORAGE_KEY = "bcp_search_actions";

const isBrowser = typeof window !== "undefined";

// Get stored actions from localStorage
const getStoredActions = (): StoredAction[] => {
  if (!isBrowser) return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const actions: StoredAction[] = JSON.parse(stored);

    // Filter out actions older than 7 days
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const validActions = actions.filter(
      (action) => action.timestamp >= sevenDaysAgo,
    );

    if (validActions.length !== actions.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validActions));
    }

    return validActions;
  } catch (error) {
    console.error("Error reading stored actions:", error);
    return [];
  }
};

const saveActions = (actions: StoredAction[]): void => {
  if (!isBrowser) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
  } catch (error) {
    console.error("Error saving actions:", error);
  }
};

const countActions = (actions: StoredAction[]): ActionCounts => {
  return actions.reduce(
    (counts, action) => {
      counts[action.type]++;
      return counts;
    },
    { search: 0, cardClick: 0, suggestedQuestionClick: 0 },
  );
};

const sendRulePostMessages = (counts: ActionCounts): void => {
  if (!isBrowser) return;

  const ruleCounts = {
    countRule1: counts.search,
    countRule2: counts.cardClick,
    countRule3: counts.suggestedQuestionClick,
  };
  parent.postMessage(ruleCounts, "*");
};

export const trackAction = (type: ActionType): void => {
  if (!isBrowser) return;

  const actions = getStoredActions();

  actions.push({
    type,
    timestamp: Date.now(),
  });

  saveActions(actions);

  const counts = countActions(actions);
  sendRulePostMessages(counts);
};

export const initializeSatisfactionTracking = (): void => {
  if (!isBrowser) return;

  // Check rules on page load
  const actions = getStoredActions();
  const counts = countActions(actions);
  sendRulePostMessages(counts);
};
